import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";
import { Alert, Card } from "react-bootstrap";

import { Checkbox, Loader, Table, TablePagination } from "@launchmetrics/lm-ui";

function AdvancedTable({ selectable = false, hidePagination = false, ...props }) {
	const { columns, pageSize, data, initialError } = props;
	const { onSearch, onResults, onError, onSelectRows } = props;

	const [searchParams, setSearchParams] = useState();
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState(data);
	const [error, setError] = useState(initialError);

	const [selectedRows, setSelectedRows] = useState([]);

	useEffect(() => {
		setError(initialError);
	}, [initialError]);

	useEffect(() => {
		let cancel = false;

		if (!searchParams) return;

		const search = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await onSearch(searchParams);
				if (cancel) return;

				const gotResult = onResults(response);
				setResult(gotResult);
			} catch (error) {
				if (typeof onError === 'function') {
					onError(error);
				} else {
					setError(error);
				}
			} finally {
				setLoading(false);
			}
		};
		search();
		return () => cancel = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	useEffect(() => {
		if (!selectable && !onSelectRows) return;
		onSelectRows(selectedRows);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRows]);

	const onSubmit = (formParams) => {
		setSearchParams({ pageSize: pageSize || 20, ...searchParams, page: 1, ...formParams });
	};

	const onTableChange = React.useCallback((action, value) => {
		if (!["pageSize", "page"].includes(action)) return;
		onSubmit({ ...searchParams, [action]: value });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	return (
		<Card>
			<Card.Header>
				{props.children && React.cloneElement(props.children, { paginatedSearchLoading: loading, onSubmit, ...props })}
			</Card.Header>
			<Card.Body>
				{
					error ?
						<Alert variant="danger">{typeof error === 'string' ? error : (error.message || error.toString())}</Alert>
						:
						<TableResults
							columns={columns}
							result={result || data}
							loading={loading}
							searchParams={searchParams}
							onTableChange={onTableChange}
							hidePagination={hidePagination}
							selectable={selectable}
							selectedRows={selectedRows}
							setSelectedRows={setSelectedRows}
						/>
				}
			</Card.Body>
		</Card>
	);
}

function TableResults(props) {
	const { columns, result, loading, searchParams, onTableChange, hidePagination, selectable, selectedRows, setSelectedRows } = props;

	const handleSelectAll = React.useCallback((event) => {
		if (!result) return;

		let newSelectedRows = [];
		if (event.target.checked) {
			newSelectedRows = result.rows.map(row => row.id);
		}

		setSelectedRows(newSelectedRows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [result]);

	const handleSelectRow = React.useCallback((event) => {
		let newSelectedRows;
		const id = event.target.id;
		if (selectedRows.includes(id)) {
			newSelectedRows = selectedRows.filter(rowId => rowId !== id);
		} else {
			newSelectedRows = [...selectedRows, id];
		}

		setSelectedRows(newSelectedRows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRows]);

	const customizeHeaders = React.useMemo(() => {
		if (!result) return null;
		if (!selectable) return columns;

		return [
			{
				name: 'selectable',
				alias: (
					<>
						<Checkbox
							checked={selectedRows.length === result.rows.length}
							indeterminate={selectedRows.length > 0 && selectedRows.length !== result.rows.length}
							onChange={handleSelectAll}
						/>
					</>
				),
			},
			...columns
		];
	}, [selectable, selectedRows, columns, result, handleSelectAll])


	const customizeRows = React.useMemo(() => {
		if (!result) return null;
		if (!selectable) return result.rows;

		return result.rows.map(row => ({
			selectable: <Checkbox id={row.id} checked={selectedRows.includes(row.id)} onChange={handleSelectRow} />,
			...row
		}));
	}, [selectable, result, selectedRows, handleSelectRow]);

	if (!result) return null;
	if (result.total === 0) return result.notFoundMessage ? result.notFoundMessage : <span>No results found</span>;

	return (
		<>
			<Table
				fluid={true}
				headers={customizeHeaders}
				rows={customizeRows}
			/>
			{
				!hidePagination && (
					<div className="d-flex flex-row justify-content-end align-items-center  mt-3 gap-3">
						{loading && <Loader size={40} />}
						<TablePagination
							onRowsPerPageChange={rowsPerPage => onTableChange("pageSize", rowsPerPage)}
							onPageChange={nextPage => onTableChange("page", nextPage)}
							count={result.total}
							rowsPerPage={(searchParams && searchParams.pageSize) || 20}
							page={(searchParams && searchParams.page) || 1}
						/>
					</div>
				)
			}
		</>
	);
}

AdvancedTable.propTypes = {
	columns: PropTypes.array.isRequired,
	data: PropTypes.shape({
		total: PropTypes.number.isRequired,
		rows: PropTypes.array,
		notFoundMessage: PropTypes.element
	}),
	pageSize: PropTypes.number,
	children: PropTypes.element,
	hidePagination: (props, propName, componentName) => {
		if (props[propName] && typeof props[propName] !== 'boolean') {
			return new Error(`Invalid prop ${propName} supplied to ${componentName}. Expected boolean.`);
		}

		if (!props[propName]) {
			if (typeof props.onSearch !== 'function') {
				return new Error(`Invalid prop onSearch supplied to ${componentName}. Expected function.`);
			}

			if (typeof props.onResults !== 'function') {
				return new Error(`Invalid prop onResults supplied to ${componentName}. Expected function.`);
			}
		}
	},
	onSearch: PropTypes.func,
	onResults: PropTypes.func,
	selectable: (props, propName, componentName) => {
		if (props[propName] && typeof props[propName] !== 'boolean') {
			return new Error(`Invalid prop ${propName} supplied to ${componentName}. Expected boolean.`);
		}

		if (props[propName]) {
			if (typeof props.onSelectRows !== 'function') {
				return new Error(`Invalid prop onSelectRows supplied to ${componentName}. Expected function.`);
			}
		}
	},
	onSelectRows: PropTypes.func
}

export default AdvancedTable;
